"use client";

import { usePathname } from "next/navigation";
import React from "react";
import { stripLocaleFromPathname } from "@/lib/i18n/routing";

type Props = {
  children: React.ReactNode;
};

/**
 * Wraps app sections (dashboard, builder, etc.) with a glass-friendly backdrop.
 * Skips the marketing home ("/") to preserve its custom hero gradient.
 */
export function AppBackground({ children }: Props) {
  const pathname = stripLocaleFromPathname(usePathname() ?? "/").pathnameWithoutLocale;
  const isHome = pathname === "/";
  const marketingExact = ["/privacy", "/terms", "/pricing"];
  const marketingPrefixes = [
    "/sign-in",
    "/sign-up",
    "/tools",
    "/blog",
    "/onboarding",
    "/resume-builder",
    "/cover-letter-generator",
    "/job-finder",
    "/resume-checker",
    "/interview-prep",
    "/resume-examples",
  ];
  const isMarketing =
    marketingExact.includes(pathname) ||
    marketingPrefixes.some((prefix) => pathname.startsWith(prefix));

  if (isHome) return <>{children}</>;

  if (isMarketing) {
    return (
      <div className="relative isolate min-h-screen w-full overflow-hidden text-slate-900 dark:text-slate-100">
        <div className="pointer-events-none fixed inset-0 -z-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#eef2ff] via-white to-[#e0f2fe] dark:from-[#040713] dark:via-[#050d24] dark:to-[#020515]" />
          <div
            className="absolute -top-64 right-1/4 h-[46rem] w-[46rem] rounded-full bg-[radial-gradient(circle_at_top,_rgba(79,70,229,0.32),_rgba(255,255,255,0))] blur-[140px] dark:bg-[radial-gradient(circle_at_top,_rgba(129,140,248,0.38),_rgba(7,11,22,0))]"
            aria-hidden
          />
          <div
            className="absolute -left-24 top-[32rem] h-[52rem] w-[52rem] rounded-full bg-[radial-gradient(circle_at_center,_rgba(56,189,248,0.24),_rgba(255,255,255,0))] blur-[160px] opacity-90 dark:bg-[radial-gradient(circle_at_center,_rgba(14,165,233,0.32),_rgba(4,7,15,0))]"
            aria-hidden
          />
          <div
            className="absolute bottom-[-40%] right-[18%] h-[48rem] w-[48rem] rounded-full bg-[radial-gradient(circle_at_bottom,_rgba(236,72,153,0.26),_rgba(255,255,255,0))] blur-[160px] opacity-80 dark:bg-[radial-gradient(circle_at_bottom,_rgba(244,114,182,0.32),_rgba(6,9,20,0))]"
            aria-hidden
          />
        </div>
        <div className="relative z-10 flex w-full flex-col">{children}</div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100 text-slate-900 dark:from-[#040713] dark:via-[#0a1026] dark:to-black dark:text-slate-100">
      {/* Glow orbs for glass feel */}
      <div className="pointer-events-none absolute -left-10 top-10 h-72 w-72 rounded-full bg-cyan-200/35 blur-3xl dark:bg-cyan-500/10" />
      <div className="pointer-events-none absolute right-[-60px] top-[-80px] h-80 w-80 rounded-full bg-indigo-200/40 blur-3xl dark:bg-indigo-500/15" />
      <div className="pointer-events-none absolute bottom-10 left-16 h-64 w-64 rounded-full bg-emerald-200/40 blur-3xl dark:bg-emerald-500/12" />

      {/* Subtle noise overlay for depth */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.6),transparent_35%),radial-gradient(circle_at_80%_0%,rgba(148,163,184,0.25),transparent_30%)] opacity-70 mix-blend-soft-light dark:opacity-40" />

      <div className="relative z-10 flex w-full flex-col">
        {children}
      </div>
    </div>
  );
}
